import { trackCallClick } from "@/lib/ga4";
import type { HubChannelsProps } from "@/types/help-hub/presentation";
export type { HubChannel } from "@/types/help-hub/presentation";
import { hubBrandTheme } from "../brandInk";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

/**
 * The other ways in, as one quiet row.
 *
 * In v1 these were four big tiles competing with each other, and the Genie —
 * the only thing on this page nobody else has — was one of them. Here the Genie
 * owns the hero and everything else is a strip beneath it: still visible, so
 * nobody feels trapped, but no longer pretending to be equals.
 *
 * A channel that isn't live on this Genie is never passed in, so a business
 * with no phone number still gets a row that looks deliberate.
 */
export const HubChannels = ({ channels, tone = "onBrand", branding }: HubChannelsProps) => {
  const theme = hubBrandTheme(branding);
  if (channels.length === 0) return null;

  const onBrand = tone === "onBrand";
  const className = onBrand
    ? "group flex min-h-11 min-w-0 max-w-full items-center gap-2.5 rounded-xl border border-white/25 bg-white/10 px-4 py-2.5 text-left transition-colors hover:bg-white/20"
    : "group flex min-h-11 min-w-0 max-w-full items-center gap-2.5 rounded-full border border-genie-border bg-white px-4 py-2.5 text-left transition-shadow hover:shadow-md";

  return (
    <div className={`flex flex-wrap items-center gap-2.5 ${onBrand ? "justify-start" : "justify-center"}`}>
      {channels.map((channel) => {
        const body = (
          <>
            <GenieIcon
              name={channel.genieIcon}
              size={18}
              color={onBrand ? "#FFFFFF" : theme.primary}
              secondaryColor={onBrand ? undefined : theme.secondary}
              gradientEnabled={!onBrand && theme.gradientOn}
              label=""
            />
            <span className="flex min-w-0 flex-col break-words leading-tight">
              <span
                className={
                  onBrand
                    ? "text-[13.5px] font-semibold text-white"
                    : "text-[13.5px] font-semibold text-genie-text-primary"
                }
              >
                {channel.label}
              </span>
              {channel.detail && (
                <span
                  className={
                    onBrand ? "text-[11.5px] text-white/70" : "text-[11.5px] text-genie-text-muted"
                  }
                >
                  {channel.detail}
                </span>
              )}
            </span>
          </>
        );

        return channel.href ? (
          <a
            key={channel.key}
            href={channel.href}
            className={className}
            onClick={channel.href.startsWith("tel:") ? () => trackCallClick("hub_channels") : undefined}
          >
            {body}
          </a>
        ) : (
          <button key={channel.key} type="button" onClick={channel.onClick} className={className}>
            {body}
          </button>
        );
      })}
    </div>
  );
};

export default HubChannels;
